import { LinkIcon, SearchIcon, Trash2Icon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  type MapCandidate,
  type MappingRow,
  getMapCandidates,
  getTitleMappings,
  mapCandidate,
  setMapping,
} from "../lib/api";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

/**
 * Ręczne wskazanie, skąd brać ten tytuł.
 *
 * Powstało, bo komunikat „zmapuj pozycję ręcznie w panelu" był ślepym zaułkiem:
 * odsyłał do ekranu, na którym widać wyłącznie **kolejkę przeglądu**, czyli
 * pozycje, których automat nie dopasował. Tytuł, którego żadne źródło nie
 * przysłało, w tej kolejce nie wisi w ogóle — więc rada była niewykonalna.
 *
 * Okno działa w dwie strony i to jest jego sens: przypina nowe źródło ORAZ odpina
 * złe. Pozycja przypięta ręcznie znika z kolejki przeglądu (`is_manual` ją stamtąd
 * zdejmuje), więc bez odpinania stąd pomyłka administratora nie miałaby jak zostać
 * naprawiona.
 */
export function MapSourcesDialog({
  titleId,
  titleRomaji,
  open,
  onOpenChange,
  onChanged,
}: {
  titleId: string;
  /** Wypełnia pole szukania — romaji trafia w serwisy częściej niż tytuł polski. */
  titleRomaji: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wołane po każdej zmianie — strona tytułu odświeża siatkę odcinków. */
  onChanged: () => void;
}) {
  const [query, setQuery] = useState(titleRomaji);
  const [candidates, setCandidates] = useState<MapCandidate[] | null>(null);
  const [current, setCurrent] = useState<MappingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reloadCurrent = useCallback(() => {
    getTitleMappings(titleId)
      .then((data) => setCurrent(data.items))
      .catch(() => setCurrent([]));
  }, [titleId]);

  useEffect(() => {
    if (!open) return;
    setQuery(titleRomaji);
    setCandidates(null);
    setError(null);
    reloadCurrent();
  }, [open, titleRomaji, reloadCurrent]);

  /**
   * Szukanie **na żądanie**, nie przy pisaniu.
   *
   * Każde wywołanie idzie do KAŻDEGO włączonego serwisu, więc podpowiadanie
   * w trakcie pisania oznaczałoby kilkanaście żądań na jedno zapytanie —
   * niegrzeczne wobec serwisów i wolne dla klikającego.
   */
  async function look(): Promise<void> {
    const needle = query.trim();
    if (needle.length < 2) {
      setError("Podaj co najmniej dwa znaki");
      return;
    }
    setBusy("szukanie");
    setError(null);
    setCandidates(null);
    try {
      const data = await getMapCandidates(needle);
      setCandidates(data.items);
    } catch (e: unknown) {
      setCandidates([]);
      setError(e instanceof ApiError ? e.message : "Nie udało się poszukać");
    } finally {
      setBusy(null);
    }
  }

  async function attach(candidate: MapCandidate): Promise<void> {
    setBusy(`${candidate.sourceId}:${candidate.slug}`);
    setError(null);
    try {
      await mapCandidate(titleId, {
        sourceId: candidate.sourceId,
        slug: candidate.slug,
        label: candidate.label,
        url: candidate.url,
      });
      reloadCurrent();
      onChanged();
      // Odświeżamy listę wyników, żeby przypięta pozycja przestała wyglądać na wolną.
      void look();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się przypiąć");
    } finally {
      setBusy(null);
    }
  }

  async function detach(row: MappingRow): Promise<void> {
    setBusy(row.id);
    setError(null);
    try {
      // `null` to jawne „ta pozycja nie ma odpowiednika" — odpięcie i zarazem
      // decyzja, która blokuje automat przed ponownym przypisaniem jej tutaj.
      await setMapping(row.id, null);
      reloadCurrent();
      onChanged();
    } catch (e: unknown) {
      setError(e instanceof ApiError ? e.message : "Nie udało się odpiąć");
    } finally {
      setBusy(null);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-3xl">
        <AlertDialogTitle className="flex items-center gap-2">
          <LinkIcon className="size-5 text-accent" />
          Skąd brać ten tytuł
        </AlertDialogTitle>
        <AlertDialogDescription>
          Wskaż pozycję w serwisie, która odpowiada tej serii. Wskazanie ręczne jest nietykalne dla
          automatu i widzą je wszyscy.
        </AlertDialogDescription>

        {error !== null && <p className="text-sm text-destructive">{error}</p>}

        {/* Obecne wskazania na górze: zanim ktoś doda kolejne, ma zobaczyć, co już
            jest — a najczęstszą przyczyną złej siatki odcinków jest wskazanie
            błędne, nie brakujące. */}
        {current.length > 0 && (
          <section className="flex flex-col gap-2">
            <h3 className="text-sm font-semibold">Obecne wskazania</h3>
            <ul className="flex flex-col gap-2">
              {current.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2 text-sm"
                >
                  <Badge variant="secondary">{row.sourceLabel}</Badge>
                  <span className="min-w-0 flex-1 truncate">{row.providerLabel}</span>
                  {row.matchReason === "manual" && <Badge variant="accent">ręczne</Badge>}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    onClick={() => void detach(row)}
                  >
                    <Trash2Icon />
                    Odepnij
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Poszukaj w źródłach</h3>
          <div className="flex gap-2">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void look();
              }}
              placeholder="Tytuł w serwisie"
              aria-label="Tytuł do wyszukania w źródłach"
            />
            <Button variant="secondary" disabled={busy !== null} onClick={() => void look()}>
              <SearchIcon />
              {busy === "szukanie" ? "Szukam…" : "Szukaj"}
            </Button>
          </div>

          {candidates !== null && candidates.length === 0 && error === null && (
            <p className="text-sm text-muted-foreground">
              Żadne źródło nie zna tej nazwy. Spróbuj innego zapisu — serwisy trzymają tytuły po
              polsku, po angielsku i w romaji, a sufiksy sezonów bywają zapisane inaczej.
            </p>
          )}

          {candidates !== null && candidates.length > 0 && (
            <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto">
              {candidates.map((candidate) => {
                const mineAlready = candidate.mappedTitleId === titleId;
                const takenByOther =
                  candidate.mappedTitleId !== null && candidate.mappedTitleId !== titleId;
                return (
                  <li
                    key={`${candidate.sourceId}:${candidate.slug}`}
                    className="flex flex-wrap items-center gap-2 rounded-lg border border-border p-2 text-sm"
                  >
                    <Badge variant="secondary">{candidate.sourceLabel}</Badge>
                    {/*
                      Wyraźne oznaczenie wersji, o które chodziło: język bierze się
                      z deskryptora źródła, nie z domysłu o treści, a „zapas" znaczy
                      „bierzemy stąd tylko wtedy, gdy nie ma polskiego".
                    */}
                    <Badge variant={candidate.language === "pl" ? "success" : "outline"}>
                      {candidate.language === "pl" ? "PL" : "EN"}
                    </Badge>
                    {candidate.fallback && <Badge variant="warning">zapas</Badge>}
                    <span className="min-w-0 flex-1 truncate">{candidate.label}</span>

                    {/* Pozycja przypisana gdzie indziej NIE jest blokowana — bywa,
                        że to poprzednie wskazanie było błędne. Ostrzegamy i zostawiamy
                        decyzję człowiekowi, bo tylko on wie, która seria to jest. */}
                    {takenByOther && <Badge variant="warning">u innego tytułu</Badge>}

                    {mineAlready ? (
                      <Badge variant="success">przypięte</Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy !== null}
                        onClick={() => void attach(candidate)}
                      >
                        Przypnij
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <AlertDialogFooter>
          <AlertDialogCancel>Zamknij</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
